import { connect } from 'react-redux';
import { reduxForm, isPristine, formValueSelector } from 'redux-form';
import withRouter from 'react-router/lib/withRouter';
import { compose, lifecycle, withHandlers, withProps } from 'recompose';
import { pick, get, isNil, pickBy, isEmpty } from 'lodash';
import Joi from 'joi-full';
import withLoading from '../../../common/withLoading';
import { ServiceDetailsForm } from '../components/ServiceDetailsForm';
import * as taxSelectors from '../../settings/taxes/selectors';
import { getServiceCategories } from '../categories/selectors';
import withCategoriesFetcher from '../categories/fetchCategories';
import withMinuteOptions from '../hoc/withMinuteOptions';
import withFetchTaxes from '../../settings/taxes/withFetchTaxes';
import { activeShop, getActiveType, getIsCommissionBarber, getShopBrand, getShops } from '../../../auth/selectors';
import durationInitialFromService from '../utils/durationInitialFromService';
import { syncValidateData } from '../../../common/validator';
import fetchServiceOnMount from '../hoc/fetchServiceOnMount';

const durationFromValues = (values) => {
  const durationHour = parseInt((values.durationHour || 0), 10) * 60;
  const durationMinute = parseInt((values.durationMinute || 0), 10);
  return durationHour + durationMinute;
};

const pickServiceProps = values => ({
  ...pick(values, [
    'enabled',
    'cost',
    'requiresPrepaid',
    'kioskEnabled',
  ]),
  duration: durationFromValues(values),
});

const extractServiceNameAndDesc = (values) => {
  // dirty hack: en translation stored in name and desc fields according to backend API

  const { en: name, ...localizedName } = values.localizedName;
  const { en: desc, ...localizedDesc } = values.localizedDesc;

  const cleanedLocalizedName = pickBy(localizedName);
  const cleanedLocalizedDesc = pickBy(localizedDesc);

  const result = {
    name,
    desc,
    localizedName: isEmpty(cleanedLocalizedName) ? null : cleanedLocalizedName,
    localizedDesc: isEmpty(cleanedLocalizedDesc) ? null : cleanedLocalizedDesc,
  };

  // another dirty hack: if en translation is not set, set it to first non-empty translation
  // this is needed because nowhere in CoW we have logic expecting service.name or service.desc to be empty
  if (!name) {
    result.name = Object.values(localizedName).find(value => !!value);
  }
  if (!desc) {
    result.desc = Object.values(localizedDesc).find(value => !!value);
  }

  return result;
};

const schema = Joi.object()
  .keys({
    localizedName: Joi.object()
      .pattern(
        /.*/,
        [
          Joi.string()
            .required()
            .min(3)
            .label('Service name'),
        ]
      ),
    localizedDesc: Joi.object()
      .pattern(
        /.*/,
        [
          Joi.string()
            .optional()
            .allow('', null)
            .max(300)
            .label('Service description'),
        ]
      ),
    durationHour: Joi.number()
      .min(0)
      .label('Hours'),
    durationMinute: Joi.number()
      .min(0)
      .label('Minutes'),
    cost: Joi.number()
      .min(0)
      .required()
      .label('Cost'),
    enabled: Joi.boolean()
      .label('Enabled'),
    kioskEnabled: Joi.boolean()
      .label('Squire Booking (Kiosk Mode)'),
    requiresPrepaid: Joi.boolean()
      .label('Prepaid only'),
    barbers: Joi.array()
      .items(Joi.object())
      .optional(),
    shops: Joi.array()
      .items(Joi.object())
      .optional(),
    tax: Joi.array()
      .items(Joi.string()
        .guid())
      .optional()
      .allow([]),
  });

const validate = values => syncValidateData(values, schema);

const getDefaultInitialValues = (supportedLanguagesCodes = [], defaultLanguage = 'en') => ({
  enabled: true,
  requiresPrepaid: false,
  kioskEnabled: true,
  cost: 0,
  localizedName: Object.fromEntries(supportedLanguagesCodes.map(lang => [lang, lang === defaultLanguage ? '' : null])),
  localizedDesc: Object.fromEntries(supportedLanguagesCodes.map(lang => [lang, lang === defaultLanguage ? '' : null])),
});

const onSubmit = async (values, dispatch, props) => {
  if (props.pristine) {
    return props.service;
  }

  const taxesToDelete = (props.initialValues.tax || []).filter(taxId => !values.tax.includes(taxId))
    .map(id => ({ id, deleted: true }));

  const service = await props.saveService({
    ...pickServiceProps(values),
    ...extractServiceNameAndDesc(values),
    serviceCategoriesId: values.serviceCategoriesId ? [values.serviceCategoriesId] : [],
    tax: (values.tax || []).map(id => ({ id }))
      .concat(taxesToDelete),
  });

  props.addNotification({
    level: 'success',
    message: 'Service saved successfully.',
    title: 'Success',
  });

  return service;
};

const withInitialValues = withProps((ownProps) => {
  const { service, supportedLanguagesCodes = [], defaultLanguage = 'en' } = ownProps;

  if (!service) {
    return { initialValues: getDefaultInitialValues(supportedLanguagesCodes, defaultLanguage) };
  }

  const tax = (service.taxes || []).map(({ id }) => id);
  const localizedName = (service.localizedName || {});
  const localizedDesc = (service.localizedDesc || {});

  supportedLanguagesCodes.forEach((lang) => {
    localizedName[lang] = localizedName[lang] || (lang === defaultLanguage ? '' : null);
    localizedDesc[lang] = localizedDesc[lang] || (lang === defaultLanguage ? '' : null);
  });

  // dirty hack: en translation stored in name and desc fields according to backend API
  if (service.name) {
    localizedName.en = service.name;
  }

  if (service.desc) {
    localizedDesc.en = service.desc;
  }

  return {
    initialValues: {
      ...pick(service, [
        'enabled',
        'cost',
        'requiresPrepaid',
        'kioskEnabled',
      ]),
      ...durationInitialFromService(service),
      serviceCategoriesId: get(service, 'categories.0.id', ''),
      tax,
      localizedDesc,
      localizedName,
    },
  };
});

const FORM_NAME = 'serviceDetails';

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = (state) => {
  const selectedLanguagesCodes = Object
    .entries(selector(state, 'localizedName') || {})
    .filter(([, value]) => !isNil(value))
    .map(([key]) => key);
  const currentShop = activeShop(state);
  const shopBrand = getShopBrand(state);

  return ({
    taxes: taxSelectors.enabled(state),
    categories: getServiceCategories(state),
    shops: getActiveType(state) === 'barber' ? [currentShop] : getShops(state),
    isCommissionBarber: getIsCommissionBarber(state),
    pristine: isPristine(FORM_NAME)(state),
    shopBrand,
    defaultLanguage: get(currentShop, 'settings.defaultLanguage')
      || get(shopBrand, 'settings.defaultLanguage')
      || 'en',
    supportedLanguagesCodes: get(currentShop, 'settings.supportedLanguages')
      || get(shopBrand, 'settings.supportedLanguages')
      || ['en'],

    selectedLanguagesCodes: selectedLanguagesCodes.length ? selectedLanguagesCodes : ['en'],
  });
};

export default compose(
  withFetchTaxes,
  lifecycle({
    componentDidMount() {
      this.props.fetchTaxes();
    },
  }),
  fetchServiceOnMount(),
  withRouter,
  withMinuteOptions,
  withCategoriesFetcher,
  connect(mapStateToProps, null),
  withInitialValues,
  withProps(ownProps => ({
    showLoading: ownProps.isServiceLoading || ownProps.isTaxesLoading,
  })),
  withProps((ownProps) => {
    const { service, roleIdType, isCommissionBarber } = ownProps;

    if (!service) {
      return {
        isLimitedEditAccess: false,
        isLimitedCategoryEditAccess: false,
      };
    }

    const isCreatedByCurrentUser = roleIdType.id === service.createdById && roleIdType.type === service.createdBy;
    const isBarberAndNotCreator = roleIdType.type === 'barber' && !isCreatedByCurrentUser;

    const isNoBrandOrSingleShopBrand = !ownProps.shopBrand || ownProps.shopBrand.shopCount === 1;

    const isBarberWithLimitedEditAccess = isCommissionBarber || isBarberAndNotCreator;
    const isShopWithLimitedEditAccess = roleIdType.type === 'shop' && !isNoBrandOrSingleShopBrand;

    return {
      isLimitedEditAccess: isBarberWithLimitedEditAccess || isShopWithLimitedEditAccess,
      isLimitedCategoryEditAccess: isBarberAndNotCreator,
    };
  }),
  withHandlers({
    onSubmit: () => onSubmit,
    onSubmitSuccess: props => (service) => {
      if (props.roleIdType.type === 'barber') {
        props.router.push(props.redirectUrl);
      } else {
        props.router.push(`${props.redirectUrl}/${service.id}/assign`);
      }
    },
    validate: () => validate,
  }),
  reduxForm({ form: FORM_NAME }),
  withHandlers({
    onAddLanguage: props => (languageCode) => {
      props.change(`localizedDesc.${languageCode}`, '');
      props.change(`localizedName.${languageCode}`, '');
    },
    onRemoveLanguage: props => (languageCode) => {
      props.change(`localizedName.${languageCode}`, null);
      props.change(`localizedDesc.${languageCode}`, null);
    },
  }),
  withLoading
)(ServiceDetailsForm);
